import React, { useState } from 'react';
import { ErrorBoundary, useContent } from '@sparknz/journeys-common';
import {
  ActionLayer,
  Box,
  Button,
  FormDropdown,
  InfoBanner,
  InfoTextButton,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { STORE_FINDER_PAGE } from '../../../../../../shared/constants';
import { environmentVariables } from '../../../../../../shared/utils';
import { useProductDetails } from '../../../../provider';
import { ProductDetailsActionTypes } from '../../../../reducer/productDetails.reducer';
import {
  TradeInEstimatorAuthorableContentType,
  TradeInEstimatorDropdownOption,
  TradeInLayerStatus,
} from '../../../../typings';
import {
  getDeviceById,
  getDevicesByMake,
  getDiscountMessage,
  getMakes,
} from '../../utils/tradeIn.utils';

function TradeInActionLayer() {
  const { tradeInLayer, dispatch } = useProductDetails();
  const shouldShowTradeIn = tradeInLayer.status === TradeInLayerStatus.Open;

  return (
    <ActionLayer
      isOpen={shouldShowTradeIn}
      title="Get a trade-in estimate"
      onClose={() =>
        dispatch({
          type: ProductDetailsActionTypes.SetTradeInLayer,
          tradeInLayerStatus: TradeInLayerStatus.Close,
        })
      }
      status="default"
    >
      <ErrorBoundary fallback={null}>
        <TradeInActionLayerInternal />
      </ErrorBoundary>
    </ActionLayer>
  );
}

const DEFAULT_OPTION = { label: '', value: '' };

export function TradeInActionLayerInternal() {
  const [selectedMake, setSelectedMake] = useState(DEFAULT_OPTION);
  const [selectedDevice, setSelectedDevice] = useState(DEFAULT_OPTION);
  const [committedSelectedDeviceId, setCommittedSelectedDeviceId] =
    useState('');

  const { data: deviceDataContent } =
    useContent<TradeInEstimatorAuthorableContentType>({
      apiBaseUrl: environmentVariables.apiURL,
      modelId: 'tradein-model',
      contentId: 'tradein-price-list',
      variation: '',
      persistedQuery: 'tradein-model-list',
      format: 'json',
    });

  const deviceData = deviceDataContent?.data.TradeInPhonePriceList.ListOfPhones;

  const makes = getMakes(deviceData);
  const devices = getDevicesByMake(selectedMake.value, deviceData);
  const selectedDeviceData = getDeviceById(
    committedSelectedDeviceId,
    deviceData,
  );
  const isButtonDisabled =
    !selectedDevice.value || committedSelectedDeviceId === selectedDevice.value;

  const buttonText = 'Get trade-in estimate';
  const findStoreLinkText = 'Find a Spark store';

  function handleMakeChange(make: TradeInEstimatorDropdownOption) {
    if (make.value === selectedMake.value) return;
    setSelectedMake(make);
    setSelectedDevice(DEFAULT_OPTION);
    trackCustomLink(make.value);
  }

  function handleDeviceChange(device: TradeInEstimatorDropdownOption) {
    setSelectedDevice(device);
    trackCustomLink(device.label);
  }

  function handleButtonClick() {
    setCommittedSelectedDeviceId(selectedDevice.value);
    trackCustomLink(buttonText);
  }

  function handleFindStoreClick() {
    trackCustomLink(findStoreLinkText);
    window.location.assign(STORE_FINDER_PAGE);
  }

  return (
    <Box pt="s4">
      <Text variant="subheadingM" mb="s3">
        See how much you can save
      </Text>
      <Text variant="bodySMedium" mb="s2">
        Get an estimated value of your phone with our online trade-in tool. The
        actual value may vary depending on the condition of your phone.
      </Text>
      <>
        <LinkRegion regionId="filter.brand">
          <Box mb="s2">
            <FormDropdown
              options={makes}
              value={selectedMake}
              onChange={(option: TradeInEstimatorDropdownOption) =>
                handleMakeChange(option)
              }
              onFocus={() => {
                trackCustomLink('Brand');
              }}
              placeholder="Old phone make"
            />
          </Box>
        </LinkRegion>
        <LinkRegion regionId="filter.model">
          <Box mb="s2">
            <FormDropdown
              options={devices}
              value={selectedDevice}
              onChange={(option: TradeInEstimatorDropdownOption) =>
                handleDeviceChange(option)
              }
              onFocus={() => {
                trackCustomLink('Model');
              }}
              placeholder="Old phone model"
            />
          </Box>
        </LinkRegion>
        <LinkRegion regionId="tradein.tradeInAndSave">
          <Box mb="s3">
            <Button
              type="button"
              onClick={handleButtonClick}
              variant="primary"
              disabled={isButtonDisabled}
              fullWidth
            >
              {buttonText}
            </Button>
          </Box>
        </LinkRegion>
      </>
      <Box>
        {selectedDeviceData ? (
          <LinkRegion regionId="tradein.tradeInAndSave">
            <InfoBanner variant="SUCCESS">
              {`${getDiscountMessage(selectedDeviceData)}`}&nbsp;View our
              current deals to stack with your trade-in credit, then bring your
              phone into one of our stores to complete the trade-in.
              <InfoTextButton
                buttonText={findStoreLinkText}
                onClick={handleFindStoreClick}
                variant="SUCCESS"
                componentAs="a"
              />
            </InfoBanner>
          </LinkRegion>
        ) : null}
      </Box>
    </Box>
  );
}

export default TradeInActionLayer;
